import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  HttpLink,
  from,
  Observable,
  split,
} from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';

export interface ApolloClientOptions {
  getToken: () => Promise<string | null>;
  graphqlUrl: string;
  /** Pre-hydrated cache (e.g. restored from IndexedDB). Falls back to empty InMemoryCache. */
  cache?: InMemoryCache;
  /**
   * DEV-ONLY: uniform per-operation latency in ms. When > 0, every operation is
   * delayed by this amount so loading / folder background-fill / item load-more
   * are observable locally. The app passes this (gated on its Vite env);
   * the package stays framework-agnostic. Omit / 0 to disable.
   */
  devFakeDelayMs?: number;
  /** Optional telemetry sink. Injected by the app; the package stays dependency-free. */
  onTransportError?: (info: { operationName: string; statusCode?: number }) => void;
}

/**
 * What a customer sees when dunning enforcement denies a write.
 *
 * ⚠ DUPLICATED COPY, deliberately and unhappily. open-api already carries this
 *   sentence as `AUTH_EXCEPTION_ERRORS.ACCOUNT_LIMITED`
 *   (`shared/common-defs.ts`) and passes it as the exception's description — but
 *   the client only ever reads `extensions.code`, so the server's wording never
 *   reaches the browser. Kept WORD-FOR-WORD identical to the server's so the two
 *   cannot say different things; if that one is edited, edit this too.
 *   The real fix is to surface the description on the wire and read it here,
 *   which is a change to the error contract rather than to dunning.
 *
 * Not translated: this package has no i18n, and the banner beside it is
 * translated and carries the pay button.
 *
 * Exported so the app can recognise a caller's `toast.error(err.message)` carrying it
 * and drop that toast: the blocked-write dialog already says this, with a pay button.
 */
export const ACCOUNT_LIMITED_MESSAGE =
  'Your account has an outstanding payment. New screens, users and publishing are ' +
  'paused until it is settled. Your screens and content are unchanged.';

export function createApolloClient(
  getTokenOrOpts: (() => Promise<string | null>) | ApolloClientOptions,
  graphqlUrl?: string,
) {
  // Support both old signature (getToken, url) and new options object
  let opts: ApolloClientOptions;
  if (typeof getTokenOrOpts === 'function') {
    if (!graphqlUrl) {
      throw new Error(
        'createApolloClient: graphqlUrl is required with legacy (getToken, url) signature',
      );
    }
    opts = { getToken: getTokenOrOpts, graphqlUrl };
  } else {
    opts = getTokenOrOpts;
  }

  const httpLink = new HttpLink({
    uri: opts.graphqlUrl,
  });

  // ─── DEV-ONLY: uniform link-layer latency ──────────────────────────────
  // Passthrough unless the app supplied `devFakeDelayMs > 0` (gated on its Vite
  // env). Delete this block + drop `delayLink` from the chain to remove.
  const fakeDelayMs = opts.devFakeDelayMs ?? 0;
  const delayLink = new ApolloLink((operation, forward) => {
    if (fakeDelayMs <= 0) return forward(operation);
    return new Observable((observer) => {
      let innerSub: { unsubscribe: () => void } | undefined;
      const timer = setTimeout(() => {
        innerSub = forward(operation).subscribe({
          next: (value) => observer.next(value),
          error: (err) => observer.error(err),
          complete: () => observer.complete(),
        });
      }, fakeDelayMs);
      return () => {
        clearTimeout(timer);
        innerSub?.unsubscribe();
      };
    });
  });
  // ───────────────────────────────────────────────────────────────────────

  const authLink = new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
      // Check if authorization header already provided in context
      // This allows callers to bypass the token fetch (e.g., signup before setAuth)
      const existingHeaders = operation.getContext().headers;
      if (existingHeaders?.authorization) {
        forward(operation).subscribe(observer);
        return;
      }

      opts.getToken().then((token) => {
        operation.setContext({
          headers: {
            authorization: token ? `Bearer ${token}` : '',
          },
        });
        forward(operation).subscribe(observer);
      });
    });
  });

  const errorLink = new ErrorLink(({ error, operation }) => {
    if (CombinedGraphQLErrors.is(error)) {
      for (const err of error.errors) {
        // INACTIVE_ACCOUNT is tagged UNAUTHENTICATED by the backend but must NOT force logout —
        // the paywall (InactiveAccountDialog) needs the session alive so the user can pay.
        if (err.message === 'INACTIVE_ACCOUNT') {
          window.dispatchEvent(new CustomEvent('auth:account-inactive'));
          continue;
        }
        // ACCOUNT_LIMITED is the dunning enforcement guard denying ONE mutation —
        // pairing a screen, inviting a member, publishing. Like INACTIVE_ACCOUNT it
        // arrives tagged UNAUTHENTICATED, so without this branch it falls through
        // to `auth:token-expired` and force-logs-out a customer who is perfectly
        // well authenticated. That breaks the one hard promise both dunning
        // diagrams are built on — payment stays reachable at every stage — because
        // the customer is bounced to /login with no error and no route to the pay
        // page. Blocking one write must never end the session.
        if (err.message === 'ACCOUNT_LIMITED') {
          window.dispatchEvent(new CustomEvent('auth:account-limited'));
          // Rewrite the raw code into something a customer can read.
          //
          // The error still has to REACH the caller: dozens of components do
          // `toast.error(err.message)` and, more importantly, treat the absence of
          // an error as success. Swallowing it here would make a blocked save
          // report "Saved" for something that never happened — worse than an ugly
          // message. So the message is replaced in place with a known sentence, and
          // `DunningCreateBlockedDialog` (opened by the event above) dismisses any
          // toast carrying it: the customer gets ONE message, the dialog.
          //
          // GraphQLError is not frozen in practice, but the assignment is guarded:
          // a read-only message must not break the link chain.
          try {
            (err as { message: string }).message = ACCOUNT_LIMITED_MESSAGE;
          } catch {
            // Left as the raw code — ugly, but the customer is not blocked twice.
          }
          continue;
        }
        if (
          (err.extensions?.code === 'UNAUTHENTICATED' &&
            err.message !== 'INVALID_TEAM' &&
            err.message !== 'NOT_FOUND_USER') ||
          err.message === 'API_TOKEN_EXPIRED' ||
          err.message === 'API_TOKEN_INVALID'
        ) {
          window.dispatchEvent(new CustomEvent('auth:token-expired'));
        }
      }
    } else {
      try {
        opts.onTransportError?.({
          operationName: operation?.operationName ?? 'unknown',
          statusCode: (error as { statusCode?: number })?.statusCode,
        });
      } catch {
        // Telemetry must never break the link chain.
      }
    }
  });

  // Build terminal link: split subscriptions to WS, queries/mutations to HTTP
  // Derive WS URL from HTTP URL: https → wss, http → ws
  const wsUrl = opts.graphqlUrl.replace(/^http/, 'ws');
  const wsLink = new GraphQLWsLink(
    createClient({
      url: wsUrl,
      lazy: true,
      retryAttempts: Infinity,
      shouldRetry: () => true,
      connectionParams: async () => {
        const token = await opts.getToken();
        if (!token) throw new Error('No auth token — retry after Firebase initializes');
        return { authorization: `Bearer ${token}` };
      },
    }),
  );

  const terminalLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    httpLink,
  );

  return new ApolloClient({
    link: from([delayLink, errorLink, authLink, terminalLink]),
    cache: opts.cache ?? new InMemoryCache(),
  });
}
